import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { theme } from 'styles/theme';
import { nextFiscalYear, projectRowKey } from 'budget/budgetYearMigrate';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10070;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 64px;
`;

const Panel = styled.div`
  width: ${(p) => (p.$wide ? '560px' : '420px')};
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md || '8px'};
  border: 1px solid ${theme.colors.border};
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const Head = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${theme.colors.border};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

const Body = styled.div`
  padding: 12px 16px;
  overflow: auto;
  flex: 1;
  min-height: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  line-height: 1.5;
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid ${theme.colors.border};
`;

const Btn = styled.button`
  margin: 0;
  padding: 3px 8px;
  min-height: 26px;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$primary ? theme.colors.primary : theme.colors.white)};
  color: ${(p) => (p.$primary ? theme.colors.white : theme.colors.text.primary)};
`;

const ModeRow = styled.div`
  display: flex;
  gap: 16px;
  margin: 10px 0 12px;
`;

const ListBox = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  max-height: 280px;
  overflow: auto;
  padding: 4px 0;
`;

const ListToolbar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
`;

const Row = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 10px 4px ${(p) => 10 + (p.$depth || 0) * 14}px;
  cursor: pointer;
  font-size: ${theme.typography.fontSize.xs};
  &:hover {
    background: ${theme.colors.background?.light || '#F8F9FA'};
  }
`;

const Hint = styled.p`
  margin: 8px 0 0;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  line-height: 1.45;
`;

function depthOf(row, byKey) {
  let d = 0;
  let cur = row;
  let guard = 0;
  while (cur?.parentCode && guard++ < 24) {
    d += 1;
    cur = byKey.get(`${cur.budgetType || ''}::${cur.parentCode}`) || null;
  }
  return d;
}

/**
 * @param {'subject'|'project'} kind
 * @param {object} dims - 현재 연도 dims
 * @param {Array} projectRows - kind=project 일 때 선택 목록
 */
const YearMigrateDialog = ({
  open,
  kind = 'subject',
  dims,
  projectRows = [],
  structureStatus,
  onClose,
  onConfirm,
}) => {
  const [mode, setMode] = useState('all');
  const [selected, setSelected] = useState(() => new Set());

  const fromYear = String(dims?.fiscalYear || '');
  const toYear = nextFiscalYear(fromYear);

  const sortedProjects = useMemo(() => {
    const rows = [...(projectRows || [])];
    const byKey = new Map(rows.map((r) => [projectRowKey(r), r]));
    return rows
      .map((r) => ({ ...r, _depth: depthOf(r, byKey) }))
      .sort((a, b) => {
        const pa = a.pathCode || a.projectCode || '';
        const pb = b.pathCode || b.projectCode || '';
        return String(pa).localeCompare(String(pb), 'ko');
      });
  }, [projectRows]);

  useEffect(() => {
    if (!open) return;
    setMode('all');
    setSelected(new Set(sortedProjects.map(projectRowKey)));
  }, [open, sortedProjects]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(sortedProjects.map(projectRowKey)));
  const clearAll = () => setSelected(new Set());

  const handleOk = () => {
    if (kind === 'subject') {
      onConfirm?.({ mode: 'all' });
      return;
    }
    onConfirm?.({
      mode,
      selectedKeys: mode === 'selected' ? Array.from(selected) : [],
    });
  };

  return createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <Panel
        $wide={kind === 'project'}
        role="dialog"
        aria-label="차년도이관"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Head>차년도이관 — {kind === 'subject' ? '예산과목' : '사업'}</Head>
        <Body>
          <div>
            <strong>
              {fromYear}년 → {toYear}년
            </strong>
            {structureStatus ? ` · 원본 구조 상태: ${structureStatus}` : ''}
          </div>

          {kind === 'subject' ? (
            <>
              <p style={{ margin: '10px 0 0' }}>
                과목구조와 예산과목 마스터를 <strong>전체</strong> 이관합니다.
                <br />
                차년도 구조는 작성중으로 저장되며, 과목은 내부코드(subjectUid)를 유지합니다.
              </p>
              <Hint>
                차년도 구조가 이미 확정되어 있으면 이관할 수 없습니다. 과목은 차년도 저장분을 덮어씁니다.
              </Hint>
            </>
          ) : (
            <>
              <p style={{ margin: '10px 0 0' }}>
                사업구조는 항상 전체 이관하고, 사업 마스터는 범위를 선택할 수 있습니다.
              </p>
              <ModeRow>
                <label>
                  <input
                    type="radio"
                    name="migrateMode"
                    checked={mode === 'all'}
                    onChange={() => setMode('all')}
                  />{' '}
                  사업 전체 이관
                </label>
                <label>
                  <input
                    type="radio"
                    name="migrateMode"
                    checked={mode === 'selected'}
                    onChange={() => setMode('selected')}
                  />{' '}
                  사업 선택 이관
                </label>
              </ModeRow>
              {mode === 'selected' && (
                <>
                  <ListToolbar>
                    <Btn type="button" onClick={selectAll}>
                      전체선택
                    </Btn>
                    <Btn type="button" onClick={clearAll}>
                      선택해제
                    </Btn>
                    <span style={{ alignSelf: 'center', fontSize: 12, color: '#6B7280' }}>
                      {selected.size} / {sortedProjects.length}
                    </span>
                  </ListToolbar>
                  <ListBox>
                    {sortedProjects.length === 0 ? (
                      <div style={{ padding: 12, color: '#6B7280' }}>이관할 사업이 없습니다.</div>
                    ) : (
                      sortedProjects.map((r) => {
                        const key = projectRowKey(r);
                        return (
                          <Row key={key} $depth={r._depth}>
                            <input
                              type="checkbox"
                              checked={selected.has(key)}
                              onChange={() => toggle(key)}
                            />
                            <span>
                              [{r.levelName || '-'}] {r.projectCode} {r.projectName}
                              {r.budgetType ? ` · ${r.budgetType}` : ''}
                            </span>
                          </Row>
                        );
                      })
                    )}
                  </ListBox>
                  <Hint>
                    선택 이관 시 상위 사업은 자동 포함됩니다. 차년도에 동일 내부코드(projectUid)가 있으면
                    건너뛰고, 없는 건만 추가합니다.
                  </Hint>
                </>
              )}
              {mode === 'all' && (
                <Hint>
                  전체 이관 시 차년도 사업 목록을 원본 연도 사업으로 대체합니다. 내부코드(projectUid)는
                  유지됩니다.
                </Hint>
              )}
            </>
          )}
        </Body>
        <Foot>
          <Btn type="button" onClick={onClose}>
            취소
          </Btn>
          <Btn type="button" $primary onClick={handleOk}>
            이관
          </Btn>
        </Foot>
      </Panel>
    </Overlay>,
    document.body
  );
};

export default YearMigrateDialog;
